import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { ThemeContextType, ThemeMode, AccentColor, TranslationMode, PreferredTranslator, User } from '../types';
import { brandAccent } from '../constants';
import { AuthProvider, useAuth } from './AuthContext';
import { dbManager } from '../services/db_manager';
import { useData } from './DataContext';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error('useTheme must be used within a ThemeProvider');
    return context;
};

const hexToRgb = (hex: string | null | undefined): string => {
    if (!hex || typeof hex !== 'string') return '79, 70, 229';
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '79, 70, 229';
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();

    const [themeMode, setThemeMode] = useState<ThemeMode>((localStorage.getItem('themeMode') as ThemeMode) || 'dark');
    const [pdfThemeMode, setPdfThemeMode] = useState<ThemeMode>((localStorage.getItem('pdfThemeMode') as ThemeMode) || 'dark');
    const [accentColor, setAccentColorState] = useState<AccentColor>(brandAccent(localStorage.getItem('libritui_themeAccentColor')));
    const [targetLanguage, setTargetLanguageState] = useState(localStorage.getItem('targetLanguage') || 'Português');
    const [studyLanguage, setStudyLanguageState] = useState(localStorage.getItem('studyLanguage') || 'Inglês');
    const [translationMode, setTranslationModeState] = useState<TranslationMode>((localStorage.getItem('translationMode') as TranslationMode) || 'ia');
    const [preferredTranslator, setPreferredTranslatorState] = useState<PreferredTranslator>((localStorage.getItem('preferred_translator') as PreferredTranslator) || 'gemini');
    const [isSmartTranslationEnabled, setSmartTranslationEnabledState] = useState<boolean>(localStorage.getItem('isSmartTranslationEnabled') === 'true');

    const { profile } = useData();

    useEffect(() => {
        // PRIORIDADE: profile.theme_color e profile.native_language (vinda da tabela profiles)
        const dbColor = profile?.theme_color;
        const dbLang = profile?.native_language as string | undefined;

        if (dbColor) {
            const color = String(dbColor).startsWith('#') ? dbColor : `#${dbColor}`;
            // Validate if it's a valid hex before applying
            if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
                setAccentColorState(brandAccent(color));
                localStorage.setItem('libritui_themeAccentColor', brandAccent(color));
            }
        }

        if (dbLang && dbLang !== targetLanguage) {
            const changedAt = parseInt(sessionStorage.getItem('language_changed_at') || '0', 10);
            if (Date.now() - changedAt < 15000) {
                // Ignore stale D1 read just after a change
                return;
            }
            setTargetLanguageState(dbLang);
            localStorage.setItem('targetLanguage', dbLang);
            localStorage.setItem('i18nextLng', dbLang);
            import('../i18n').then(module => {
                const i18n = module.default;
                if (i18n.language !== dbLang) {
                    i18n.changeLanguage(dbLang);
                }
            });
        }
    }, [profile]);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', themeMode === 'dark');
        const rgb = hexToRgb(accentColor);
        document.documentElement.style.setProperty('--primary-rgb', rgb);
        document.documentElement.style.setProperty('--main-color', accentColor);
    }, [themeMode, accentColor]);

    const saveSetting = async (currentUser: User | null, updates: any) => {
        // NO-OP: Deprecated. Settings are now handled directly via profiles table.
    };

    const value: ThemeContextType = {
        themeMode, isDarkMode: themeMode === 'dark', isPdfDarkMode: pdfThemeMode === 'dark',
        toggleGlobalTheme: () => {
            const next = themeMode === 'light' ? 'dark' : 'light';
            setThemeMode(next); setPdfThemeMode(next);
            localStorage.setItem('themeMode', next); localStorage.setItem('pdfThemeMode', next);
        },
        toggleAppOnlyTheme: () => {
            const next = themeMode === 'light' ? 'dark' : 'light';
            setThemeMode(next); localStorage.setItem('themeMode', next);
        },
        togglePdfOnlyTheme: () => {
            const next = pdfThemeMode === 'light' ? 'dark' : 'light';
            setPdfThemeMode(next); localStorage.setItem('pdfThemeMode', next);
        },
        setTheme: (m: ThemeMode) => { setThemeMode(m); localStorage.setItem('themeMode', m); },
        accentColor,
        setAccentColor: async (color: string) => {
            const validColor = color.startsWith('#') ? color : `#${color}`;
            if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(validColor)) {
                setAccentColorState(validColor);
                localStorage.setItem('libritui_themeAccentColor', validColor);
                await saveSetting(user, { theme_color: validColor, accent_color: validColor });
            }
        },
        targetLanguage,
        setTargetLanguage: (lang: string) => {
            // Accept either a language code (en, fr, es...) or a display name
            const validCodes = ['en', 'pt', 'fr', 'de', 'it', 'es', 'ja', 'zh', 'ru', 'hu'];
            let langCode = validCodes.includes(lang) ? lang : '';

            // If not a direct code, try mapping from display names (any language)
            if (!langCode) {
                const nameToCode: Record<string, string> = {
                    // Portuguese names
                    'Inglês': 'en', 'Português': 'pt', 'Francês': 'fr',
                    'Alemão': 'de', 'Italiano': 'it', 'Espanhol': 'es',
                    'Japonês': 'ja', 'Mandarim': 'zh', 'Chinês': 'zh',
                    'Russo': 'ru', 'Húngaro': 'hu',
                    // English names
                    'English': 'en', 'Portuguese': 'pt', 'French': 'fr',
                    'German': 'de', 'Italian': 'it', 'Spanish': 'es',
                    'Japanese': 'ja', 'Mandarin': 'zh', 'Chinese': 'zh',
                    'Russian': 'ru', 'Hungarian': 'hu',
                    // French names
                    'Anglais': 'en', 'Portugais': 'pt', 'Français': 'fr',
                    'Allemand': 'de', 'Italien': 'it', 'Espagnol': 'es',
                    'Japonais': 'ja', 'Russe': 'ru', 'Hongrois': 'hu',
                    // Spanish names
                    'Inglés': 'en', 'Portugués': 'pt', 'Francés': 'fr',
                    'Alemán': 'de', 'Español': 'es',
                    'Japonés': 'ja', 'Mandarín': 'zh', 'Ruso': 'ru',
                    // Italian names
                    'Inglese': 'en', 'Portoghese': 'pt', 'Francese': 'fr',
                    'Tedesco': 'de', 'Spagnolo': 'es',
                    'Giapponese': 'ja', 'Mandarino': 'zh', 'Ungherese': 'hu',
                    // German names
                    'Englisch': 'en', 'Portugiesisch': 'pt', 'Französisch': 'fr',
                    'Deutsch': 'de', 'Italienisch': 'it', 'Spanisch': 'es',
                    'Japanisch': 'ja', 'Russisch': 'ru', 'Ungarisch': 'hu',
                    // Japanese names
                    '英語': 'en', 'ポルトガル語': 'pt', 'フランス語': 'fr',
                    'ドイツ語': 'de', 'イタリア語': 'it', 'スペイン語': 'es',
                    '日本語': 'ja', '中国語': 'zh', 'ロシア語': 'ru', 'ハンガリー語': 'hu',
                    // Chinese names
                    '英语': 'en', '葡萄牙语': 'pt', '法语': 'fr',
                    '德语': 'de', '意大利语': 'it', '西班牙语': 'es',
                    '日语': 'ja', '普通话': 'zh', '俄语': 'ru', '匈牙利语': 'hu',
                    // Russian names
                    'Английский': 'en', 'Португальский': 'pt', 'Французский': 'fr',
                    'Немецкий': 'de', 'Итальянский': 'it', 'Испанский': 'es',
                    'Японский': 'ja', 'Китайский': 'zh', 'Русский': 'ru', 'Венгерский': 'hu',
                    // Hungarian names
                    'Angol': 'en', 'Portugál': 'pt', 'Francia': 'fr',
                    'Német': 'de', 'Olasz': 'it', 'Spanyol': 'es',
                    'Japán': 'ja', 'Kínai': 'zh', 'Orosz': 'ru', 'Magyar': 'hu',
                };
                langCode = nameToCode[lang] || 'pt';
            }

            // Dispatch loading event to show the UI barrier INSTANTLY
            window.dispatchEvent(new CustomEvent('show-global-loading', { 
                detail: { 
                    messageKey: 'settings.loading_language', 
                    fallback: 'Configurando seu LibriTui...' 
                } 
            }));

            // Save the language CODE (not display name) for persistence
            setTargetLanguageState(lang);
            localStorage.setItem('targetLanguage', lang);
            localStorage.setItem('i18nextLng', langCode);
            sessionStorage.setItem('language_changed_at', Date.now().toString());
            const authKey = Object.keys(localStorage).find(key => key.includes('sb-auth-token') || key.includes('auth-token'));
            const session = authKey ? localStorage.getItem(authKey) : null;
            const userProfile = localStorage.getItem('libritui_user');
            const flashcardQueue = localStorage.getItem('libritui_flashcard_queue');
            const themeMode = localStorage.getItem('themeMode');
            const pdfThemeMode = localStorage.getItem('pdfThemeMode');
            const accentColorVal = localStorage.getItem('libritui_themeAccentColor');
            const appVersion = localStorage.getItem('app_version');
            const lastAccepted = localStorage.getItem('last_accepted_version');

            const doReload = () => {
                // Clear caches then reload
                (async () => {
                    try {
                        if ('caches' in window) {
                            const names = await caches.keys();
                            // Only clear translation-related caches
                            await Promise.all(
                                names
                                    .filter(name => name !== 'whisper-stt-cache' && name !== 'nllb-onnx-models-v1' && name !== 'free-dict-cache' && name !== 'rich-dict-cache-v2' && name !== 'bergamot-models-v1' && name !== 'marian-onnx-models-v1' && name !== 'transformers-cache' && name !== 'piper-voices-cache')
                                    .map(name => caches.delete(name))
                            );
                        }
                    } catch (e) { /* ignore */ }
                    
                    // Restore essentials after cache clear
                    localStorage.setItem('targetLanguage', lang);
                    localStorage.setItem('i18nextLng', langCode);
                    if (session && authKey) localStorage.setItem(authKey, session);
                    if (userProfile) localStorage.setItem('libritui_user', userProfile);
                    if (flashcardQueue) localStorage.setItem('libritui_flashcard_queue', flashcardQueue);
                    if (themeMode) localStorage.setItem('themeMode', themeMode);
                    if (pdfThemeMode) localStorage.setItem('pdfThemeMode', pdfThemeMode);
                    if (accentColorVal) localStorage.setItem('libritui_themeAccentColor', accentColorVal);
                    if (appVersion) localStorage.setItem('app_version', appVersion);
                    if (lastAccepted) localStorage.setItem('last_accepted_version', lastAccepted);

                    // Update i18n
                    import('../i18n').then(module => {
                        const i18n = module.default;
                        if (i18n.language !== langCode) {
                            i18n.changeLanguage(langCode);
                        }
                    });

                    // Wait 500ms before soft reset
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('hide-global-loading'));
                        // Limpar a rota (Soft Reset para /)
                        window.history.pushState({}, '', '/');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                    }, 500);
                })();
            };

            if (user && !user.isGuest) {
                // Update local cache immediately to prevent stale data on reload
                const cacheKey = `data_profile_${user.id}`;
                const localStr = localStorage.getItem(cacheKey);
                if (localStr) {
                    try {
                        const parsed = JSON.parse(localStr);
                        if (parsed && parsed.data) {
                            parsed.data.native_language = langCode;
                            localStorage.setItem(cacheKey, JSON.stringify(parsed));
                        }
                    } catch (e) {}
                }

                // Wait for DB save before reloading
                dbManager.from('profiles', user.id).upsert({
                    id: user.id,
                    native_language: langCode,
                    updated_at: new Date().toISOString()
                }).then(() => {
                    doReload();
                }).catch(err => {
                    console.warn('[ThemeContext] Failed to save native_language to D1:', err);
                    doReload();
                });
            } else {
                doReload();
            }
        },
        studyLanguage,
        setStudyLanguage: (lang: string) => {
            setStudyLanguageState(lang);
            localStorage.setItem('studyLanguage', lang);
            // No longer forcing D1 sync for study_language as it's being deprecated in favor of auto-detect
            // saveSetting(user, { study_language: lang });
        },
        translationMode,
        setTranslationMode: (mode: TranslationMode) => {
            setTranslationModeState(mode);
            localStorage.setItem('translationMode', mode);
        },
        preferredTranslator,
        setPreferredTranslator: (translator: PreferredTranslator) => {
            setPreferredTranslatorState(translator);
            localStorage.setItem('preferred_translator', translator);
        },
        isSmartTranslationEnabled,
        setSmartTranslationEnabled: (enabled: boolean) => {
            setSmartTranslationEnabledState(enabled);
            localStorage.setItem('isSmartTranslationEnabled', enabled.toString());
        }
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
